import {useCallback, useMemo, useState} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {SearchResults} from '../types'
import {useOnParsedQueryChange} from '../hooks/use-on-parsed-query-change'
import {useSearchResults} from '../contexts/SearchResultsContext'
import {useFeaturedListings} from '../contexts/FeaturedListingsContext'
import {useRecommendedListings} from '../contexts/RecommendedListingsContext'
import {useQuery} from '../contexts/QueryContext'
import {useCategory} from '../contexts/CategoryContext'
import {useSearchType} from '../contexts/SearchTypeContext'
import {useCopilotApp} from '../contexts/CopilotAppContext'
import {announce} from '@github-ui/aria-live'

interface FetchSearchResults {
  isSearching: boolean
  loading: boolean
  fetchSearchResults: (path: string) => Promise<void>
}

export function useFetchSearchResults(): FetchSearchResults {
  const {searchResults, setSearchResults} = useSearchResults()
  const {setFeatured} = useFeaturedListings()
  const {setRecommended} = useRecommendedListings()
  const {query} = useQuery()
  const {category} = useCategory()
  const {type} = useSearchType()
  const {copilotApp} = useCopilotApp()
  const shouldDisplaySearchResults = useMemo(() => {
    return !!(query || category || type || copilotApp)
  }, [category, copilotApp, query, type])
  const [isSearching, setIsSearching] = useState(shouldDisplaySearchResults)
  const onParsedQueryChange = useOnParsedQueryChange()
  const parsedQueryStr = searchResults.parsedQuery ? JSON.stringify(searchResults.parsedQuery) : ''
  const [loading, setLoading] = useState(false)
  const [lastLoadedPath, setLastLoadedPath] = useState<string | undefined>(undefined)

  const fetchSearchResults = useCallback(
    async (path: string) => {
      if (path === lastLoadedPath) return

      setLoading(true)
      const response = await verifiedFetchJSON(path)
      const data = await response.json()
      let newParsedQuery: SearchResults['parsedQuery']
      if (shouldDisplaySearchResults) {
        newParsedQuery = data.parsedQuery
        // This will be just search results
        setSearchResults(data)
        // tell screen readers how many results were found
        announceSearchResults(data.total)
      } else {
        // This will be the full index payload
        setFeatured(data.featured)
        setRecommended(data.recommended)
        const newSearchResults = data.searchResults ?? data.results
        newParsedQuery = newSearchResults?.parsedQuery
        setSearchResults(newSearchResults)
      }
      if (newParsedQuery && parsedQueryStr !== JSON.stringify(newParsedQuery)) onParsedQueryChange(newParsedQuery)
      setIsSearching(shouldDisplaySearchResults)
      setLoading(false)
      setLastLoadedPath(path)
    },
    [
      lastLoadedPath,
      parsedQueryStr,
      onParsedQueryChange,
      setFeatured,
      setRecommended,
      setSearchResults,
      shouldDisplaySearchResults,
    ],
  )

  return {fetchSearchResults, isSearching, loading}
}

function announceSearchResults(total: number) {
  if (total === 0) {
    // No results found
    announce('No results. Try searching by different keywords.')
  } else {
    // Results found
    announce(`${total} ${total === 1 ? 'result' : 'results.'}`)
  }
}
